import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { EVENT_BANNER_SIZE_LABEL } from "../constants/eventBanner";
import {
  getImageDimensions,
  isExactEventBannerSize,
} from "../utils/cropEventBannerImage";
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from "../uploadLimits";

/**
 * Handles event banner file selection: validates size, skips crop when already 1400×550,
 * otherwise opens the crop modal for the caller to render.
 */
export function useEventBannerUpload(onFileReady) {
  const [cropSession, setCropSession] = useState(null);
  const revokeRef = useRef(null);

  const clearCropSession = useCallback(() => {
    if (revokeRef.current) {
      revokeRef.current();
      revokeRef.current = null;
    }
    setCropSession(null);
  }, []);

  useEffect(() => () => clearCropSession(), [clearCropSession]);

  const handleFileSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";

      if (!file) return;

      if (file.size > IMAGE_UPLOAD_MAX_BYTES) {
        toast.error(
          `Image exceeds the ${IMAGE_UPLOAD_MAX_MB}MB size limit. Maximum upload size is ${IMAGE_UPLOAD_MAX_MB}MB per image.`
        );
        return;
      }

      try {
        const { width, height } = await getImageDimensions(file);
        if (isExactEventBannerSize(width, height)) {
          onFileReady(file);
          return;
        }
      } catch {
        toast.error("Could not read the selected image. Please try another file.");
        return;
      }

      clearCropSession();
      const src = URL.createObjectURL(file);
      revokeRef.current = () => URL.revokeObjectURL(src);
      setCropSession({ src, fileName: file.name });
    },
    [clearCropSession, onFileReady]
  );

  const handleCropConfirm = useCallback(
    (croppedFile) => {
      clearCropSession();
      onFileReady(croppedFile);
      toast.success(`Banner cropped to ${EVENT_BANNER_SIZE_LABEL}.`);
    },
    [clearCropSession, onFileReady]
  );

  const handleCropCancel = useCallback(() => {
    clearCropSession();
  }, [clearCropSession]);

  return {
    cropSession,
    handleFileSelect,
    handleCropConfirm,
    handleCropCancel,
  };
}
